import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { FiAlertTriangle, FiCheck, FiChevronDown, FiCrosshair, FiDownload, FiX } from "react-icons/fi";
import { GAMEGUARD_URL } from "../../../lib/constants";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Spinner } from "./primitives";

/*
 * ReadinessBar: одна область «причина + единственное основное действие».
 * Модель считает Stage из существующих правил; здесь только отображение.
 */
export type CtaModel = {
  kind: "link-steam" | "search" | "ready" | "cancel" | "retry";
  label: string;
  pendingLabel?: string;
  pending?: boolean;
  disabled?: boolean;
  quiet?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};
export type Reason = { tone: "block" | "ok" | "info" | "error"; text: string };

const Bar = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  min-width: 0;
`;
const Text = styled.div`
  flex: 1 1 260px;
  max-width: 56ch;
  min-width: 0;
  display: grid;
  gap: 6px;
`;
const ReasonLine = styled.p<{ $tone: Reason["tone"] }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 15px;
  line-height: 22px;
  font-weight: 500;
  color: ${(p) =>
    p.$tone === "block"
      ? p.theme.colors.warning
      : p.$tone === "ok"
        ? p.theme.colors.success
        : p.$tone === "error"
          ? p.theme.colors.danger
          : p.theme.colors.textSecondary};
  svg {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    margin-top: 2px;
  }
`;
const More = styled.div`
  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 8px 0 0;
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
    svg {
      width: 14px;
      height: 14px;
      transition: transform ${(p) => p.theme.motion.fast};
    }
    &[aria-expanded="true"] svg {
      transform: rotate(180deg);
    }
    &:hover {
      color: ${(p) => p.theme.colors.text};
    }
  }
  ul {
    list-style: none;
    display: grid;
    gap: 4px;
    margin-top: 4px;
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  li::before {
    content: "–";
    margin-right: 8px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const CtaSlot = styled.div`
  @media (max-width: 560px) {
    width: 100%;
  }
`;
const Note = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => p.theme.colors.textMuted};
  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.primary};
    svg {
      width: 14px;
      height: 14px;
    }
    &:hover {
      color: ${(p) => p.theme.colors.primaryHover};
      text-decoration: underline;
    }
  }
`;
const sheen = keyframes`
  0%, 55% { transform: translateX(-120%) skewX(-20deg); }
  100% { transform: translateX(260%) skewX(-20deg); }
`;
export const CtaButton = styled.button<{ $quiet?: boolean }>`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 240px;
  height: 52px;
  padding: 0 28px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 650;
  white-space: nowrap;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.onPrimary};
  transition:
    background ${(p) => p.theme.motion.fast},
    transform ${(p) => p.theme.motion.fast} ${(p) => p.theme.motion.ease};
  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
  &:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.primaryHover};
  }
  &:active:not(:disabled) {
    transform: scale(0.94);
    transition-duration: 60ms;
  }
  &:focus-visible {
    outline-offset: 4px;
  }
  /* Главная кнопка живёт: тёплое свечение и блик, который пробегает по ней. */
  &.search:not(:disabled) {
    background: linear-gradient(110deg, #ff8a3d 0%, #ffb27a 50%, #ff8a3d 100%) 0 0 / 200% 100%;
    box-shadow: 0 6px 22px rgba(255, 155, 87, 0.3);
    &::after {
      content: "";
      position: absolute;
      inset: -2px auto -2px 0;
      width: 38%;
      z-index: -1;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
      animation: ${sheen} 3.2s ease-in-out infinite;
    }
    &:hover {
      transform: translateY(-2px) scale(1.03);
      background-position: 100% 0;
      transition:
        transform 0.18s cubic-bezier(0.2, 1.4, 0.4, 1),
        background-position 0.6s;
    }
  }
  &:disabled {
    cursor: not-allowed;
    background: ${(p) => p.theme.colors.input};
    color: ${(p) => p.theme.colors.textMuted};
  }
  ${(p) =>
    p.$quiet &&
    css`
      background: ${p.theme.colors.surfaceHover};
      color: ${p.theme.colors.text};
      &:hover:not(:disabled) {
        background: #2d3b4b;
      }
    `}
  @media (max-width: 560px) {
    width: 100%;
    min-width: 0;
  }
`;

export function Cta({ cta, id }: { cta: CtaModel; id?: string }) {
  const icon =
    cta.kind === "search" ? <FiCrosshair aria-hidden="true" /> : cta.kind === "cancel" ? <FiX aria-hidden="true" /> : cta.kind === "ready" && !cta.quiet ? <FiCheck aria-hidden="true" /> : null;
  return (
    <CtaButton
      id={id}
      type="button"
      className={cta.kind}
      $quiet={cta.quiet}
      disabled={cta.disabled || cta.pending}
      aria-describedby="home-reason"
      onClick={cta.onClick}
    >
      {cta.pending ? (
        <>
          <Spinner aria-hidden="true" />
          {cta.pendingLabel || cta.label}
        </>
      ) : (
        <>
          {icon}
          {cta.label}
        </>
      )}
    </CtaButton>
  );
}

export default function ReadinessBar({
  reason,
  more,
  cta,
  loading,
  ctaHidden,
  ctaRef,
}: {
  reason: Reason | null;
  more: string[];
  cta: CtaModel;
  loading: boolean;
  /** true, пока то же действие показано в закреплённой панели: исключает два CTA одновременно. */
  ctaHidden: boolean;
  ctaRef: React.Ref<HTMLDivElement>;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <Bar aria-label={t("home.readiness")} role="group">
      <Row>
        <Text>
          {loading ? (
            <ReasonLine $tone="info" id="home-reason" role="status">
              {t("home.loading_team")}
            </ReasonLine>
          ) : (
            reason && (
              <ReasonLine $tone={reason.tone} id="home-reason" role={reason.tone === "error" ? "alert" : undefined}>
                {reason.tone === "block" || reason.tone === "error" ? (
                  <FiAlertTriangle aria-hidden="true" />
                ) : reason.tone === "ok" ? (
                  <FiCheck aria-hidden="true" />
                ) : null}
                {reason.text}
              </ReasonLine>
            )
          )}
          {more.length > 0 && (
            <More>
              <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
                {open ? t("home.reasons_hide") : t("home.more_reasons", { n: more.length })}
                <FiChevronDown aria-hidden="true" />
              </button>
              {open && (
                <ul>
                  {more.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
            </More>
          )}
        </Text>
        <CtaSlot ref={ctaRef} aria-hidden={ctaHidden || undefined} {...(ctaHidden ? ({ inert: "" } as object) : {})}>
          <Cta cta={cta} />
        </CtaSlot>
      </Row>
      <Note>
        {t("home.gameguard_note")}
        <a href={GAMEGUARD_URL} target="_blank" rel="noopener noreferrer">
          <FiDownload aria-hidden="true" />
          {t("home.gameguard_download")}
        </a>
      </Note>
    </Bar>
  );
}
